<template>
  <b-container fluid>
    <template v-if="hasApiCallFailed">
      <something-went-wrong text="View all licenses" />
    </template>
    <template v-else>
      <div id="page-top">
        <b-row>
          <search
            @findLicenseByChange="handleFindLicenseByChange"
            @searchInputChange="handleSearchInputChange"
            @orderDateChange="handleOrderDateChange"
            @licenseTypeChange="handleLicenseTypeChange"
            @fromDateChange="handleFromDateChange"
            @toDateChange="handleToDateChange"
            @advancedSearchButtonClick="handleAdvancedSearchButtonClick"
            @onSearchClick="handleSearchClick"
            v-bind="search"
            :isAdvancedSearchVisible="search.showAdvancedSearch"
          />
        </b-row>
        <b-row>
          <quick-find @filterClicked="setLicenseFilter" page="All licenses" ref="quickFind" />
        </b-row>
        <div v-if="isInternal && !isPersonInRole">
          <a href="https://citrix.service-now.com/sp?id=sc_cat_item&sys_id=713158551b8c50500bfeb912cd4bcbab" target="_blank">
            <b-row>
              <b-col class="not-permitted-text">
                <h5>{{ $t('contactCustomerCareReturnInternal')}}</h5>
              </b-col>
            </b-row>
          </a>
        </div>
        <template v-if="!loading">
          <b-row class="all-licenses-table-row">
            <div class="col-12 col-md-3">
              <b-select
                :value="actionOption"
                :options="actionOptions"
                class="select-action-dropdown"
                @change.native="handleActionSelection"
                :disabled="selectedLicenses.length === 0 || hasError || hasNoRecords || (isInternal && !isPersonInRole)"
                ref="actionDropdown"
                :title="selectedLicenses.length === 0 ? $t('noSelectionInfoText') : ''"
              />
            </div>
            <div class="col-12 d-sm-block d-md-none options-btn-container">
              <a href="#" @click.stop.prevent="showOptions = !showOptions">
                {{ $t('viewHideOptions', { action: !showOptions ? $t('view') : $tc('hide', 1) } ) }}
              </a>
            </div>
            <div :class="['col-12 col-md-9 options-container', { show: showOptions }]">
              <div class="row">
                <div class="col-sm-12 col-md">
                  <fieldset class="form-group">
                    <legend>{{ $t('filterBy') }}</legend>
                    <div class="select-container">
                      <b-select
                        v-model="filterOption"
                        :options="filterOptions"
                        @change="handleFilterChange"
                      />
                    </div>
                  </fieldset>
                </div>
                <div class="col-sm-12 col-md">
                  <fieldset class="form-group">
                    <legend>{{ $t('sortBy') }}</legend>
                    <div class="select-container">
                      <b-select
                        v-model="sortOption"
                        :options="sortOptions"
                        @change="handleSortChange"
                      />
                    </div>
                  </fieldset>
                </div>
                <div class="col-sm-12 col-md page-size-col">
                  <fieldset class="form-group">
                    <legend>{{ $t('show') }}</legend>
                    <div class="select-container">
                      <b-select
                        v-model="pageSizeOption"
                        @change="handlePageNumberChange"
                        :options="pageSizeOptions"
                      />
                    </div>
                  </fieldset>
                </div>
                <div class="col-sm-12 col-md-4 date-picker">
                  <fieldset class="form-group">
                    <legend>{{ $t('dateRange') }}</legend>
                    <div class="select-container">
                      <b-input
                        :value="dateRangeValue"
                        disabled
                      />
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
          </b-row>
          <b-row v-if="hasNoRecords" class="no-records-container">
            <h3>{{ $t('noRecordsFound') }}</h3>
          </b-row>
          <b-row class="all-licenses-table-header vertical-padding left-spacing" v-if="!hasNoRecords">
            <div class="col checkbox-col">
              <b-checkbox @change="toggleAllLicensesSelection" v-model="areAllLicensesSelected" />
            </div>
            <div class="col">
              <b-row>
                <div class="col indent-text">
                  {{ $t('productName') }}
                </div>
                <div class="d-none d-md-block col dynamic-column-select-container header">
                  {{ dynamicColumnHeader }}
                  &nbsp;&nbsp;&nbsp;
                  <i class="fa fa-caret-down" />
                  <b-select
                    :options="dynamicColumnOptions"
                    v-model="dynamicColumnOption"
                    class="dynamic-column-select"
                  />
                </div>
                <div class="d-none d-md-block col allocate-col text-right">
                  {{ $t('allocated') }}
                </div>
              </b-row>
            </div>
          </b-row>
          <template v-for="(license, i) in licenses">
            <b-row :key="license.ProductName + i" :class="getTableRowClasses(license, i)">
              <div class="col checkbox-col">
                <b-checkbox
                  @change="toggleLicenseSelection(license)"
                  :checked="isLicenseSelected(license)"
                  :class="isLicenseInvalid(license) ? 'error-checkbox' : ''"
                />
              </div>
              <div class="col">
                <b-row>
                  <div class="col indent-text">
                    <a
                      :href="`/#/license/${license.ActivationID}`"
                      :title="license.ProductName"
                      @click.stop.prevent="handleLicenseLinkClick(license)"
                    >
                      {{ license.ProductName }}
                    </a>
                    <span v-if="isLicenseNew(license)" class="success-text">{{ $t('new') }}</span>
                    <span v-else-if="isLicenseExpiringLow(license)" class="danger-text">
                      {{ $tc('expiring', 1) }}
                    </span>
                    <span v-else-if="isLicenseExpiringHigh(license)" class="warning-text">
                      {{ $tc('expiring', 1) }}
                    </span>
                    <p v-if="isLicenseInvalid(license)" class="error-row-text">
                      {{ $t('multipleSelectionHostTypeError', { hostType }) }}
                    </p>
                    <p v-if="isFutureLicense(license)" class="warning-text">
                    {{ $t('futureDateLicenseError', { StartDate:getReadableDate(license.StartDate) }) }}
                    </p>
                  </div>
                  <div
                    class="d-none d-md-block col dynamic-column-select-container"
                    :title="dynamicColumnOption === 'HostValue' ? dynamicColumnItem(license) : ''"
                  >
                    {{ dynamicColumnItem(license) }}
                  </div>
                  <div class="d-none d-md-block col allocate-col text-right">
                    {{ getAllocatedText(license) }}
                  </div>
                </b-row>
              </div>
            </b-row>
          </template>
          <b-row class="all-licenses-table-footer" v-if="!hasNoRecords">
            <div class="col-6 d-none d-md-block">
              <a
                href="#"
                class="export-link"
                @click.stop.prevent="handleExportLinkClick"
              >
                {{ $t('exportToExcel') }}
              </a>
            </div>
            <div class="col-12 col-md-6 text-center">
              <div class="pagination-container">
                <b-pagination
                  :value="currentPage"
                  @change="handlePageChange"
                  :limit="6"
                  :per-page="pageSizeOption"
                  hide-goto-end-buttons
                  :prev-text="$t('paginationPrevText')"
                  :next-text="$t('paginationNextText')"
                  :total-rows="totalLicensesCount"
                  ref="pagination"
                />
              </div>
            </div>
          </b-row>
          <b-row v-if="!isInternal || (isInternal && isPersonInRole)">
            <a href="#" @click.stop.prevent="webkeyModal = true" class="margin-bottom: 10px">
              {{ $t('dontSeeLicenses') }}
            </a>
          </b-row>
        </template>
        <template v-if="loading">
          <loading />
        </template>
      </div>
    </template>
    <b-modal v-model="showPagingWarning" @hide="handleModalClose">
      <div slot="modal-title">
        {{ $t('bulkActions') }}
      </div>
      <b-container class="modal-content-container">
        <b-row class="text-center">
          <div class="col-12">
            <h5 class="danger-text">{{ $t('bulkActionErrorTitle') }}</h5>
            <h6 v-html="$t('bulkActionErrorInfo')" />
          </div>
        </b-row>
        <b-row class="buttons-container">
          <div class="col-12">
            <b-button
              style="margin: 30px auto 10px; display: block;"
              variant="primary"
              size="lg"
              @click="handleModalClose(true)"
            >
              {{ $t('bulkActionCtaButtonText') }}
            </b-button>
          </div>
          <div class="col-12 text-center">
            <a href="#" @click.stop.prevent="clearLicensesAndPaginate">
              {{ $t('bulkActionSecondaryButtonText') }}
            </a>
          </div>
        </b-row>
      </b-container>
      <div slot="modal-footer" />
    </b-modal>
    <allocation-modal
      :modal="allocateModal"
      :closeModal="closeAllocateModal"
      :openModal="openAllocateModel"
      :selectedLicenses="selectedLicenses"
      :getUpdatedLicenses="getUpdatedLicenses"
    />
    <hide-unhide-modal
      :modal="hideUnhideModal"
      :closeModal="closeHideUnhideModal"
      :selectedLicenses="selectedLicenses"
      :type="hideUnhideModalType"
      :onSuccessfulAction="resetActionOption"
    />
    <return-allocation-modal
      isFromViewAll
      :modal="returnModal"
      :closeModal="closeReturnModal"
      :selectedLicenses="selectedLicenses"
    />
    <webkey-allocation-modal
      :modal="webkeyModal"
      :closeModal="closeWebkeyModal"
      :webkeyModalCallback="webkeyModalCallback"
      :allocateFlowCallback="allocateFlowCallback"
      :webkey="webkey"
    />
    <error-modal
      :disableCta="disableErrorModalCta"
      :modal="errorModal"
      :closeModal="closeErrorModal"
      :errorType="errorModalType"
      :handleCtaClick="errorModalCtaClickHandler"
    />
  </b-container>
</template>

<script>
  import Vue from 'vue'
  import moment from 'moment'
  import download from 'downloadjs'
  import csv from 'papaparse'
  import axios from 'axios'
  import { mapGetters, mapMutations } from 'vuex'

  import Loading from './Loading'
  import SomethingWentWrong from './SomethingWentWrong'
  import Datepicker from './Datepicker'
  import SearchMixin from '../mixins/SearchMixin'
  import DateFormatMixin from '../mixins/DateFormatMixin'
  import ErrorModal from './ErrorModal'
  import HideUnhideModal from './HideUnhideModal'
  import ReturnAllocationModal from './ReturnAllocationModal'
  import AllocationModal from './AllocationModal'
  import WebKeyAllocationModal from './WebKeyAllocationModal'
  import SurveyModal from './SurveyModal'
  axios.defaults.withCredentials = false

  export default {
    name: 'AllLicenses',
    components: {
      'loading': Loading,
      'something-went-wrong': SomethingWentWrong,
      'datepicker': Datepicker,
      'error-modal': ErrorModal,
      'hide-unhide-modal': HideUnhideModal,
      'return-allocation-modal': ReturnAllocationModal,
      'allocation-modal': AllocationModal,
      'webkey-allocation-modal': WebKeyAllocationModal,
      'survey-modal': SurveyModal
    },
    mixins: [SearchMixin, DateFormatMixin],
    mounted () {
      Promise.all([
        this.fetchValidSurveyUrl('Allocate'),
        this.fetchValidSurveyUrl('XSAllocate')
      ]
      )
      .then((responses) => {
        console.log('response', responses)
        const hasData = responses.some(response => response && response.Url)
        console.log('hasData', hasData)

        // Allocate Result
        var result = responses.find(response => response.Action === 'Allocate')
        console.log('result', result)
        if (result && result.Url) {
          this.VALID_SURVEY_DATA({
            surveyId: result.Id,
            surveyUrl: result.Url
          })
          this.$store.getters.surveyUrl = result.Url
          this.$store.getters.surveyId = result.Id
          console.log('VALID_SURVEY_DATA', {
            surveyId: result.Id,
            surveyUrl: result.Url
          })
        } else {
          this.SHOW_SURVEY_MODAL(false)
        }

        // XSAllocate Result
        var xsAllocationSurvey = responses.find(response => response.Action === 'XSAllocate')
        console.log('xsAllocateionSurvey', xsAllocationSurvey)
        if (xsAllocationSurvey && xsAllocationSurvey.Url) {
          this.VALID_SURVEY_DATA_XS({
            xsSurveyId: xsAllocationSurvey.Id,
            xsSurveyUrl: xsAllocationSurvey.Url
          })
          this.$store.getters.xsSurveyUrl = xsAllocationSurvey.Url
          this.$store.getters.xsSurveyId = xsAllocationSurvey.I
        } else {
          this.SHOW_SURVEY_MODAL_XS(false)
        }
        console.log('VALID_SURVEY_DATA_XS', {
          xsSurveyId: xsAllocationSurvey.Id,
          xsSurveyUrl: xsAllocationSurvey.Url
        })
      }
      )
      .catch((error) => {
        this.loggly.log({
          errorMessage: `Failed `,
          errorObject: error,
          errorStack: error.stack
        })
      })
      // const i = this.$root.$on('XenServerAllocate', function () {
      //   i.fetchValidSurveyUrl('XSAllocate')
      // })
      const { search, isAdvancedSearchVisible, quickFind, landing, showWebkeyModal } = this.$route.params
      const { webkey } = this.$route.query
      if (showWebkeyModal) {
        this.webkeyModal = true
      }
      if (!!webkey) { // eslint-disable-line
        this.webkeyModal = true
        this.webkey = webkey
      }
      if (quickFind && quickFind.length > 0) {
        this.quickFind = quickFind
        this.setLicenseFilter(quickFind)
      }
      if (search && isAdvancedSearchVisible !== undefined) {
        this.search = search
        this.wasSearchPerformed = true
        this.isAdvancedSearchVisible = isAdvancedSearchVisible
        if (this.search.findLicenseByOption !== 'ProductName') {
          this.dynamicColumnOption = this.search.findLicenseByOption
        }
      }
      if (landing) {
        this.filterOption = landing
        this.sortOption = landing === 'expiringLicenses' ? 'ExpirationDate asc' : 'StartDate asc'
      }
      if (this.$route.params.licenseFromBetaEval && this.$route.params.isNewOrAllocated) {
        if (this.$route.params.isNewOrAllocated === 'allocated') {
          this.search.searchInput = this.$route.params.licenseFromBetaEval
          this.handleSearchClick()
        } else if (this.$route.params.isNewOrAllocated === 'new') {
          this.webkey = this.$route.params.licenseFromBetaEval
          this.webkeyModal = true
        }
      } else {
        this.getUpdatedLicenses()
        this.sortOption = ''
      }
    },
    created () {
      Vue.AppInsights.trackPageView('All licenses page', '/licenses')
    },
    data () {
      return {
        isInternal: window.location.host.includes('citrite.net'),
        loading: false,
        hasApiCallFailed: false,
        currentPage: 1,
        showOptions: false,
        actionOption: '',
        filterOption: '',
        sortOption: '',
        sortOptions: [
          { value: 'StartDate desc', text: this.$t('newestFirst') },
          { value: 'StartDate asc', text: this.$t('oldestFirst') },
          { value: 'ProductName asc', text: this.$t('productAsc') },
          { value: 'ProductName desc', text: this.$t('productDesc') },
          { value: 'SerialNumber asc', text: this.$t('licenseAsc') },
          { value: 'SerialNumber desc', text: this.$t('licenseDesc') },
          { value: 'HostValue asc', text: this.$t('hostIdAsc') },
          { value: 'HostValue desc', text: this.$t('hostIdDesc') }
        ],
        pageSizeOption: 25,
        pageSizeOptions: [25, 50, 75, 100],
        dateRangeValue: null,
        licenses: [],
        areAllLicensesSelected: false,
        selectedLicenses: [],
        dynamicColumnOption: 'SerialNumber',
        dynamicColumnOptions: [
          { value: 'SerialNumber', text: this.$t('licenseAccessCode') },
          { value: 'SAPSalesOrderNumber', text: this.$tc('orderNumber', 2) },
          { value: 'HostValue', text: this.$tc('hostId', 1) }
        ],
        totalLicensesCount: 0,
        quickFind: null,
        fromDate: null,
        toDate: null,
        hasError: false,
        minDate: null,
        maxDate: null,
        pageToLoad: null,
        showPagingWarning: false,
        errorModal: false,
        errorModalType: '',
        hideUnhideModal: false,
        returnModal: false,
        allocateModal: false,
        webkeyModal: false,
        hideUnhideAltered: false,
        wasSearchPerformed: false,
        webkey: ''
      }
    },
    computed: {
      ...mapGetters([
        'isPersonInRole',
        'isSurveyModalInteracted',
        'isSurveyModalInteractedXS',
        'showSurveyModal',
        'showSurveyModalXS',
        'validXSSurveyURL',
        'validSurveyData'
      ]),
      areAllActionsDisabled () {
        if (
          this.filterOption === 'archived' ||
          this.quickFind === 'archived' ||
          this.search.licenseTypeOption === 'archived'
        ) {
          return true
        }
        if (this.selectedLicenses.some(l => l.IsArchived)) {
          return true
        }
        return false
      },
      actionOptions () {
        const dummyOption = { value: '', text: this.$t('selectAnAction'), disabled: true }
        const actionOptions = [
          {
            value: 'downloadLicenseFiles',
            text: this.$tc('downloadLicenseFiles', 2),
            disabled: this.areAllActionsDisabled
          },
          {
            value: 'allocateLicenses',
            text: this.$t('allocateLicenses'),
            disabled: this.areAllActionsDisabled
          },
          {
            value: 'returnAllocations',
            text: this.$t('returnAllocations'),
            disabled: this.areAllActionsDisabled
          },
          {
            value: 'archiveLicenses',
            text: this.$t('archiveLicenses'),
            disabled: this.areAllActionsDisabled
          }
        ]
        const unhideOption = {
          value: 'undoArchiveLicenses',
          text: this.$t('undoArchiveLicenses'),
          disabled: !this.licenses.every(l => l.IsArchived)
        }
        if (
          this.filterOption === 'archived' ||
          this.quickFind === 'archived' ||
          this.search.licenseTypeOption === 'archived'
        ) {
          return [dummyOption, unhideOption]
        }
        return [dummyOption, ...actionOptions]
      },
      filterOptions () {
        return [
          { value: 'expiringLicenses', text: this.$t('expiringLicenses') },
          { value: 'newPurchases', text: this.$t('newLicenses') },
          { value: 'availableAllocations', text: this.$t('availableAllocations') },
          { value: 'trialLicenses', text: this.$t('trialLicenses') }
        ]
      },
      isSearchInputPresent () {
        return this.search.searchInput !== ''
      },
      searchBy () {
        if (this.quickFind !== null) {
          return null
        }
        return this.isSearchInputPresent ? this.search.findLicenseByOption : null
      },
      searchByValue () {
        if (this.quickFind !== null) {
          return null
        }
        return this.isSearchInputPresent ? this.search.searchInput.trim() : null
      },
      areFromAndToDatesPresent () {
        const { fromDateValue, toDateValue } = this.search
        return fromDateValue !== '' && toDateValue !== ''
      },
      advSearchBy () {
        if (this.quickFind !== null) {
          return null
        }
        return this.areFromAndToDatesPresent ? this.search.dateOption : null
      },
      advDateFrom () {
        if (this.quickFind !== null) {
          return null
        }
        const { fromDateValue } = this.search
        return fromDateValue !== '' ? fromDateValue : null
      },
      advDateTo () {
        if (this.quickFind !== null) {
          return null
        }
        const { toDateValue } = this.search
        return toDateValue !== '' ? toDateValue : null
      },
      advState () {
        if (this.quickFind !== null) {
          return null
        }
        return this.areFromAndToDatesPresent ? this.search.licenseTypeOption : null
      },
      sortBy () {
        if (this.filterOption === 'expiringLicenses' && this.sortOption === '') {
          const { sortOption } = this
          this.sortOption = 'ExpirationDate asc'
          setTimeout(() => {
            this.sortOption = sortOption
          }, 1000)
        }
        return this.sortOption ? this.sortOption.split(' ')[0] : null
      },
      requestBody () {
        if (this.quickFind !== null) {
          this.resetSearchToDefaultValues()
        }
        return {
          customerID: this.$store.getters.person.customerId,
          duration: 0,
          noOfItems: this.pageSizeOption,
          pageNumber: this.currentPage,
          pageIndex: 0,
          searchBy: this.searchBy,
          searchByValue: this.searchByValue,
          advSearchBy: this.advSearchBy,
          advDateFrom: this.advDateFrom,
          advDateTo: this.advDateTo,
          advState: this.advState,
          quickFind: this.quickFind,
          filterBy: this.filterOption || null,
          sortBy: this.sortBy,
          sortByOrder: this.sortOption ? this.sortOption.split(' ')[1] : null,
          dateFrom: this.fromDate,
          dateTo: this.toDate
        }
      },
      dynamicColumnHeader () {
        return this.dynamicColumnOptions.find(o => o.value === this.dynamicColumnOption).text
      },
      hasNoRecords () {
        return this.licenses.length === 0
      },
      loggedInUserName () {
        return this.$store.getters.authToken.userName || ''
      },
      loggedInUserPersonID () {
        return this.$store.getters.authToken.personId || ''
      },
      hostType () {
        return this.selectedLicenses.length > 0
          ? this.$t(this.selectedLicenses[0].HostType.toLowerCase())
          : ''
      },
      errorModalCtaClickHandler () {
        const closeModalAndProceed = (value) => {
          this.closeErrorModal()
          this.handleActionSelection({ target: { value } })
        }
        switch (this.errorModalType) {
          case 'allocate':
            return () => {
              this.selectedLicenses = this.selectedLicenses.filter(l => l.RemainingUserCount > 0)
              closeModalAndProceed('allocateLicenses')
            }
          case 'download':
            return () => {
              this.selectedLicenses = this.selectedLicenses
                .filter(l => l.UserCount !== l.RemainingUserCount)
              closeModalAndProceed('downloadLicenseFiles')
            }
          case 'return':
            return () => {
              this.selectedLicenses = this.selectedLicenses
                .filter(l => l.UserCount !== l.RemainingUserCount)
              closeModalAndProceed('returnAllocations')
            }
          case 'hide':
            return () => {
              this.selectedLicenses = this.selectedLicenses.filter(l => !l.IsArchived)
              closeModalAndProceed('archiveLicenses')
            }
          case 'unhide':
            return () => {
              this.selectedLicenses = this.selectedLicenses.filter(l => l.IsArchived)
              closeModalAndProceed('archiveLicenses')
            }
          default:
            return () => {}
        }
      },
      hideUnhideModalType () {
        if (
          this.filterOption === 'archived' ||
          this.quickFind === 'archived' ||
          this.search.licenseTypeOption === 'archived'
        ) {
          return 'unhide'
        }
        return 'hide'
      },
      isAllActiveMode () {
        return this.quickFind === 'active'
      },
      disableErrorModalCta () {
        if (this.errorModalType === 'allocate') {
          return this.selectedLicenses.every((l) => l.RemainingUserCount === 0)
        } else {
          return this.selectedLicenses.every((l) => l.UserCount === l.RemainingUserCount)
        }
      }
    },
    methods: {
      ...mapMutations([
        'VALID_SURVEY_DATA',
        'SHOW_SURVEY_MODAL',
        'SHOW_SURVEY_MODAL_XS',
        'SURVEY_MODAL_CLOSED',
        'SURVEY_MODAL_CLOSED_XS',
        'VALID_SURVEY_DATA_XS'
      ]),
      resetSearchToDefaultValues () {
        this.search.showAdvancedSearch = false
        this.search.findLicenseByOption = 'SerialNumber'
        this.search.searchInput = ''
        this.search.fromDateValue = ''
        this.search.toDateValue = ''
        this.search.licenseTypeOption = 'active'
        this.search.dateOption = 'StartDate'
      },
      getReadableDate (date) {
        const temp = moment(date).format(this.getDateFormatBasedOnLocale())
        return (temp === '01/01/0001') || (temp === '0001/01/01') ? this.$t('notApplicable') : temp
      },
      validateMultipleSelection () {
        let hostType
        if (
          this.filterOption === 'archived' ||
          this.quickFind === 'archived' ||
          this.search.licenseTypeOption === 'archived'
        ) return
        if (!this.selectedLicenses.length) return
        hostType = this.selectedLicenses[0].HostType
        this.selectedLicenses = this.selectedLicenses.map(l => {
          if (l.HostType === hostType) {
            delete l.isValid
            return l
          } else {
            return { ...l, isValid: false }
          }
        })
        this.hasError = this.selectedLicenses.some(l => l.hasOwnProperty('isValid'))
      },
      isLicenseInvalid (license) {
        return (this.isLicenseSelected(license, true) || {}).hasOwnProperty('isValid')
      },
      getUpdatedLicenses () {
        this.loading = true
        return this.bottle
          .post(this.UrlBuilder.allLicensesUrl, this.requestBody)
          .then(({ data }) => {
            this.licenses = data.Licenses || []
            this.totalLicensesCount = data.TotalCount
            this.loading = false
            this.minDate = data.MinDate
            this.maxDate = data.MaxDate
            this.setLicenseInfo()
          })
          .catch((error) => {
            this.loading = false
            this.hasApiCallFailed = true
            this.loggly.log({
              errorMessage: 'Failed to fetch all licenses',
              errorObject: error,
              errorStack: error.stack
            })
          })
      },
      calculateDayDifference (date, expiring = false) {
        let isDateLessThanToday, from, to, number
        number = expiring ? 0 : 1
        date = new Date(date)
        date = date.setTime(
          Date.UTC(
            date.getFullYear(), date.getMonth(), date.getDate(),
            date.getHours(), date.getMinutes(), date.getSeconds()
          )
        )
        isDateLessThanToday = Date.now() < new Date(date).getTime()
        if (moment().isSame(moment(date), 'day')) {
          return 0
        }
        if (expiring && !isDateLessThanToday) {
          return -1
        }
        from = isDateLessThanToday ? moment(date) : moment()
        to = isDateLessThanToday ? moment().subtract(1, 'days') : moment(date)
        return from.diff(to, 'days') + number
      },
      isLicenseNew (item) {
        const dayDifference = this.calculateDayDifference(item.StartDate)
        return dayDifference >= 0 && dayDifference <= 30
      },
      isFutureLicense (item) {
        if (this.isLicenseSelected(item, true)) {
          let date = new Date(item.StartDate)
          date = date.setTime(
          Date.UTC(
            date.getFullYear(), date.getMonth(), date.getDate(),
            date.getHours(), date.getMinutes(), date.getSeconds()
          )
        )
          return moment(date).isAfter(moment(), 'day')
        } else {
          return false
        }
      },
      isLicenseExpiringLow (item) {
        const dayDifference = this.calculateDayDifference(item.ExpirationDate, true)
        return dayDifference >= 0 && dayDifference <= 29
      },
      isLicenseExpiringHigh (item) {
        const dayDifference = this.calculateDayDifference(item.ExpirationDate, true)
        return dayDifference > 29 && dayDifference <= 89
      },
      getTableRowClasses (item, i) {
        const classes = ['vertical-padding', 'table-row', i % 2 === 1 ? 'odd-row' : '']
        if (this.isLicenseNew(item)) {
          classes.push('new-license-row')
        } else if (this.isLicenseExpiringLow(item)) {
          classes.push('expiring-license-row-low')
        } else if (this.isLicenseExpiringHigh(item)) {
          classes.push('expiring-license-row-high')
        } else if (this.isLicenseInvalid(item)) {
          classes.push('error-row', 'left-spacing')
        } else {
          classes.push('left-spacing')
        }
        return classes
      },
      handleFilterChange (filter) {
        this.filterOption = filter
        this.currentPage = 1
        this.handleInteraction('filter')
        this.getUpdatedLicenses()
      },
      handleSortChange (value) {
        this.sortOption = value
        this.currentPage = 1
        this.handleInteraction('sort')
        this.getUpdatedLicenses()
        if (value.split(' ')[0] === 'HostValue') {
          this.dynamicColumnOption = 'HostValue'
        } else {
          this.dynamicColumnOption = 'SerialNumber'
        }
      },
      handleInteraction (interactionType) {
        switch (interactionType) {
          case 'action':
            Vue.AppInsights.trackEvent('License action', {
              eventType: 'change',
              actionValue: this.actionOption,
              page: 'View all licenses'
            })
            break
          case 'exportToCSV':
            Vue.AppInsights.trackEvent('Export to CSV', {
              eventType: 'click',
              page: 'View all licenses'
            })
            break
          case 'filter':
            Vue.AppInsights.trackEvent('Filter', {
              eventType: 'change',
              filterValue: this.filterOption,
              page: 'View all licenses'
            })
            break
          case 'sort':
            Vue.AppInsights.trackEvent('Sort', {
              eventType: 'change',
              sortValue: this.sortOption,
              page: 'View all licenses'
            })
            break
          case 'date':
            Vue.AppInsights.trackEvent('Date', {
              eventType: 'change',
              dateValue: `${this.fromDate} to ${this.toDate}`,
              page: 'View all licenses'
            })
            break
          default:
            break
        }
      },
      setLicenseFilter (licenseType = null) {
        this.quickFind = licenseType
        this.currentPage = 1
        this.filterOption = ''
        this.sortOption = 'StartDate desc'
        this.dateRangeValue = ''
        this.areAllLicensesSelected = false
        this.pageSizeOption = 25
        this.wasSearchPerformed = false
        this.selectedLicenses = []
        this.getUpdatedLicenses()
        this.sortOption = ''
        this.hasError = false
      },
      getAllocatedText ({ RemainingUserCount, UserCount }) {
        return RemainingUserCount > UserCount
          ? `${UserCount} of ${UserCount}`
          : `${UserCount - RemainingUserCount} of ${UserCount}`
      },
      toggleAllLicensesSelection () {
        this.selectedLicenses = this.areAllLicensesSelected
          ? []
          : [...this.licenses]
        this.validateMultipleSelection()
      },
      toggleLicenseSelection (license) {
        if (!this.selectedLicenses.length || !this.isLicenseSelected(license)) {
          this.selectedLicenses.push(license)
        } else {
          this.selectedLicenses = this.selectedLicenses
            .filter(l => l.ActivationID !== license.ActivationID)
        }
        this.areAllLicensesSelected = this.selectedLicenses.length === this.licenses.length
        this.validateMultipleSelection()
      },
      dynamicColumnItem (license) {
        switch (this.dynamicColumnOption) {
          case 'SAPSalesOrderNumber':
            if (!license.LicenseItemNumber) {
              return `${license.SAPSalesOrderNumber}/${this.getLicenseItemNumber(license)}`
            }
            return `${license.SAPSalesOrderNumber}/${license.LicenseItemNumber}`
          case 'HostValue':
            return license.HostValue || ''
          case 'SerialNumber':
          default:
            return license.SerialNumber || ''
        }
      },
      handleAdvancedSearchButtonClick (showAdvancedSearch) {
        this.search.showAdvancedSearch = showAdvancedSearch
      },
      handleSearchClick () {
        this.quickFind = null
        this.currentPage = 1
        this.filterOption = ''
        this.dateRangeValue = ''
        this.pageSizeOption = 25
        this.sortOption = 'StartDate desc'
        this.dynamicColumnOption = this.search.findLicenseByOption === 'ProductName'
          ? 'SerialNumber'
          : this.search.findLicenseByOption
        Vue.AppInsights.trackEvent(
          this.search.showAdvancedSearch ? 'Advanced search' : 'Search',
          {
            eventType: 'click',
            searchInput: this.search.searchInput,
            searchType: this.search.findLicenseByOption,
            page: 'View all licenses'
          }
        )
        this.getUpdatedLicenses()
          .then(() => { this.wasSearchPerformed = true })
        this.sortOption = ''
      },
      paginate (page) {
        this.currentPage = page
        this.scrollToTop()
        this.getUpdatedLicenses()
      },
      scrollToTop () {
        window.scrollTo(0, document.getElementById('page-top').offsetTop)
      },
      handlePageChange (currentPage) {
        if (this.selectedLicenses.length === 0) {
          if (!this.wasSearchPerformed) {
            this.search.searchInput = ''
            this.search.fromDateValue = ''
            this.search.toDateValue = ''
          }
          this.paginate(currentPage)
        } else {
          this.pageToLoad = currentPage
          this.showPagingWarning = true
        }
      },
      handleDateChange ({ fromDate, toDate }) {
        this.fromDate = fromDate
        this.toDate = toDate
        if (this.fromDate && this.toDate) {
          this.currentPage = 1
          this.handleInteraction('date')
          this.getUpdatedLicenses()
        }
      },
      handlePageNumberChange (pageNumber) {
        this.pageSizeOption = pageNumber
        this.currentPage = 1
        this.getUpdatedLicenses()
      },
      handleExportLinkClick () {
        this.bottle.post(this.UrlBuilder.exportCsvUrl, {
          ...this.requestBody,
          noOfItems: this.totalLicensesCount
        })
          .then(({ data }) => {
            Vue.AppInsights.trackEvent('Download action', {
              ...this.requestBody,
              noOfItems: this.totalLicensesCount
            })
            try {
              data = csv.parse(data, {
                skipEmptyLines: true
              }).data
              data = data
                .map(([c1, c2, c3, c4, , c6, c7, c8, , , c11, c12, c13, , c15, c16, c17, c18, c19]) =>
                  ([this.cleanData(c1), this.cleanData(c2), this.cleanData(c3), this.cleanData(c4), this.cleanData(c6), this.cleanData(c7), this.cleanData(c8), this.cleanData(c11), this.cleanData(c12.includes('/1753') ? '' : c12), this.cleanData(c13), this.cleanData(c15), this.cleanData(c16), this.cleanData(c17), this.cleanData(c18), this.cleanData(c19)])
                )
              data = csv.unparse(data)
            } catch (error) {
              this.loggly.log({
                errorMessage: 'Error parsing the CSV data',
                errorObject: error,
                errorStack: error.stack
              })
            }
            download(data, 'licenses.csv', 'application/octet-stream')
          })
          .catch((error) => {
            this.loggly.log({
              errorMessage: 'Failed to fetch csv file for download',
              errorObject: error,
              errorStack: error.stack
            })
          })
        this.handleInteraction('exportToCSV')
      },
      cleanData (dataToClean) {
        var checkRegex = '^[=+\\-@].*'
        return dataToClean !== undefined && dataToClean !== null && dataToClean.length > 0 ? (dataToClean.match(checkRegex) ? '\'' + dataToClean : dataToClean) : dataToClean
      },
      isLicenseSelected (license, returnItem = false) {
        const selectedLicense = this.selectedLicenses
          .find(val => val.ActivationID === license.ActivationID)
        return returnItem ? selectedLicense : !!selectedLicense
      },
      setLicenseInfo () {
        this.dateRangeValue = this.getReadableDate(this.minDate) +
            ` ${this.$t('to')} ` +
            this.getReadableDate(this.maxDate)
      },
      handleModalClose (shouldFocusActionDropdown = false) {
        this.showPagingWarning = false
        if (this.$refs.pagination) {
          this.$refs.pagination.currentPage = this.currentPage
        }
        if (shouldFocusActionDropdown) {
          this.scrollToTop()
        }
      },
      clearLicensesAndPaginate () {
        this.selectedLicenses = []
        this.handleModalClose()
        this.paginate(this.pageToLoad)
      },
      handleActionSelection ({ target: { value: action } }) {
        this.handleInteraction('action')
        switch (action) {
          case 'allocateLicenses':
            if (this.selectedLicenses.some(l => l.RemainingUserCount === 0)) {
              this.errorModal = true
              this.errorModalType = 'allocate'
              break
            }
            if (this.selectedLicenses.length > 0) {
              if (this.selectedLicenses.some(item => item.ProductName.includes('XenServer')) && this.validXSSurveyURL) {
                if (this.isSurveyModalInteractedXS) {
                  this.allocateModal = true
                  this.SHOW_SURVEY_MODAL_XS(false)
                  this.SHOW_SURVEY_MODAL(false)
                } else {
                  this.allocateModal = false
                  this.SHOW_SURVEY_MODAL_XS(true)
                  this.SHOW_SURVEY_MODAL(false)
                }
              } else {
                this.allocateModal = true
                this.SHOW_SURVEY_MODAL(false)
                this.SHOW_SURVEY_MODAL_XS(false)
              }
            }
            break
          case 'downloadLicenseFiles':
            if (this.selectedLicenses.some(l => l.UserCount === l.RemainingUserCount)) {
              this.errorModal = true
              this.errorModalType = 'download'
              break
            }
            if (this.selectedLicenses.length > 0) {
              this.bottle
                .post(
                  this.UrlBuilder.getDownloadUrl(),
                  this.selectedLicenses.map((l) => ({
                    activationID: l.ActivationID,
                    customerId: this.$store.getters.person.customerId,
                    hostId: [],
                    hostType: l.HostType,
                    loggedInUserName: this.loggedInUserName,
                    loggedInUserPersonID: this.$store.getters.person.id,
                    action: 'Redownload',
                    licenseLineItemNumber: l.LicenseItemNumber,
                    productName: l.ProductName,
                    assetID: l.AssetID
                  })), {
                    responseType: 'arraybuffer'
                  }
                )
                  .then(({ data, headers }) => {
                    const fileName = this.getDownloadFilename(headers['content-disposition'])
                    switch (headers['content-type']) {
                      case 'application/zip':
                        const link = document.createElement('a')
                        link.href = window.URL.createObjectURL(
                          new Blob([data], { type: 'application/zip' })
                        )
                        link.download = fileName
                        link.click()
                        break
                      case 'text/html':
                        download(data, fileName, 'text/html')
                        break
                      default:
                        break
                    }
                    // this.showSurveyModal = true
                    this.resetActionOption()
                  })
                  .catch((error) => {
                    this.loggly.log({
                      errorMessage: 'License download failed',
                      errorObject: error,
                      errorStack: error.stack
                    })
                  })
            }
            break
          case 'returnAllocations':
            if (this.selectedLicenses.some(l => l.UserCount === l.RemainingUserCount)) {
              this.errorModal = true
              this.errorModalType = 'return'
              break
            }
            if (this.selectedLicenses.length > 0) {
              this.returnModal = true
            }
            break
          case 'archiveLicenses':
            if (this.selectedLicenses.some(l => l.IsArchived)) {
              this.errorModal = true
              this.errorModaltype = 'hide'
              break
            }
            if (this.selectedLicenses.length > 0) {
              this.hideUnhideModal = true
            }
            break
          case 'undoArchiveLicenses':
            if (this.selectedLicenses.some(l => !l.IsArchived)) {
              this.errorModal = true
              this.errorModaltype = 'unhide'
              break
            }
            if (this.selectedLicenses.length > 0) {
              this.hideUnhideModal = true
            }
            break
          default:
            break
        }
      },
      resetActionOption (clearLicenses = true) {
        if (clearLicenses) {
          this.selectedLicenses = []
        }
        this.actionOption = ''
        this.areAllLicensesSelected = false
        if (this.$refs.actionDropdown) {
          this.$refs.actionDropdown.localValue = ''
        }
      },
      handleLicenseLinkClick (license) {
        this.$store.commit('setLicenseDetails', {
          ProductName: license.ProductName,
          ExpirationDate: license.ExpirationDate,
          IsPermanent: license.IsPermanent,
          ActivationID: license.ActivationID,
          IsArchived: license.IsArchived,
          AllowpartialAllocation: license.AllowpartialAllocation
        })
        this.$router.push(`/license/${license.ActivationID}`)
      },
      closeHideUnhideModal () {
        this.hideUnhideModal = false
        this.loading = true
        this.getUpdatedLicenses()
          .then(() => {
            this.resetActionOption()
            if (this.currentPage !== 1 && this.licenses.length === 0 && !this.hideUnhideAltered) {
              this.loading = true
              this.currentPage = this.currentPage - 1
              this.areAllLicensesSelected = false
              this.hideUnhideAltered = true
            }
          })
          .then(() => this.getUpdatedLicenses())
      },
      closeReturnModal () {
        this.returnModal = false
        this.resetActionOption()
        this.getUpdatedLicenses()
      },
      closeWebkeyModal () {
        this.webkeyModal = false
      },
      closeAllocateModal () {
        this.allocateModal = false
        this.resetActionOption()
        this.getUpdatedLicenses()
      },
      openAllocateModel () {
        this.allocateModal = true
        this.SHOW_SURVEY_MODAL_XS(false)
        this.SHOW_SURVEY_MODAL(false)
      },
      getLicenseItemNumber ({ ActivationID }) {
        return ActivationID.match(/^.*-.*-(.*)/)[1]
      },
      closeErrorModal () {
        this.errorModal = false
        this.resetActionOption(false)
      },
      webkeyModalCallback (err, licenses, totalCount, minDate, maxDate) {
        if (!err) {
          this.licenses = licenses
          this.totalLicensesCount = totalCount
          this.minDate = minDate
          this.maxDate = maxDate
        }
        this.webkeyModal = false
      },
      allocateFlowCallback (licenses) {
        this.selectedLicenses = licenses
        this.allocateModal = true
      },
      getDownloadFilename (string) {
        return string.split('; ')[1].split('=')[1]
      },
      fetchValidSurveyUrl (actionType) {
        const headers = {
          apiKey: this.UrlBuilder.surveyAPIKey
        }
        var action = this.UrlBuilder.licenseActionAllocate
        if (actionType === 'XSAllocate') {
          action = this.UrlBuilder.licenseActionXSAllocate
        }
        return axios.post(this.UrlBuilder.surveyAPIUrl, {
          PersonId: this.loggedInUserPersonID,
          ApplicationName: this.UrlBuilder.surveyAppName,
          Action: action
        }, {
          headers: headers
        })
      .then(({ data }) => {
        if (data && data.Url) {
          console.log('data', data)
          const surveyData = {
            Id: data.Id,
            Url: data.Url,
            Action: data.Action
          }
          console.log('surveyData before return', surveyData)
          return surveyData
        } else {
          return {
            Id: null,
            Url: null,
            Action: data.Action
          }
        }
      })
    .catch((error) => {
      this.SHOW_SURVEY_MODAL(false)
      this.loggly.log({
        errorMessage: `Failed `,
        errorObject: error,
        errorStack: error.stack
      })
    })
      }
    }
  }
</script>


  <style scoped lang="scss">
    @import '../styles/variables.scss';

  a {
    text-decoration: none;
    color: $primary !important;
    cursor: pointer;
  }
  fieldset {
    border: 1px solid $lightGray;
    border-radius: 5px;
    padding: 12px 0 0;
    width: 100%;
  }
  fieldset > legend {
    padding: 0 10px;
    margin-left: 10px;
    width: auto;
    font-size: 0.75rem;
    color: $darkGray;
    margin-bottom: 0;
    z-index: $legendZIndex;
    position: relative;
    background-color: white;
  }
  .select-container {
    margin: -20px 0 0;
  }
  select {
    border: none !important;
    height: 30px;
    width: 100% !important;
    text-indent: 10px;
  }
  select {
    font-size: 1rem;
  }
  select:hover,
  select:focus,
  select:active {
    outline: 0 !important;
    border: none !important;
    box-shadow: none !important;
  }
  .select-action-dropdown,
  .select-action-dropdown:active,
  .select-action-dropdown:focus {
    width: 100%;
    height: 40px !important;
    margin-top: 8px;
    background-color: $primary;
    color: $white;
    padding-bottom: 8px;
  }
  .danger-text {
    color: $danger;
  }
  .select-action-dropdown > option {
    background-color: $white;
    color: $black;
  }
  .options-btn-container {
    padding: 15px;
    text-align: center;
  }
  .options-container {
    min-height: 0;
    height: 0;
    opacity: 0;
    transition: height 0.5s;
  }
  .options-container.show {
    min-height: 300px;
    height: auto;
    opacity: 1;
    transition: height 0.5s;
  }
  .license-creation-input {
    background-color: $white !important;
    border-color: transparent;
    position: relative;
  }
  .all-licenses-table-row {
    margin-left: -30px;
    margin-right: -30px;
  }
  .all-licenses-table-header {
    background-color: $lightGray;
    margin-bottom: 10px;
  }
  .vertical-padding {
    padding-top: 7px;
    padding-bottom: 7px;
  }
  .table-row {
    margin-bottom: 10px;
    border: 1px solid $lightGray;
    border-radius: 5px;
  }
  .new-license-row {
    border-left-width: 10px;
    border-left-color: $success;
  }
  .expiring-license-row-low {
    border-left-width: 10px;
    border-left-color: $danger;
  }
  .expiring-license-row-high {
    border-left-width: 10px;
    border-left-color: $warning;
  }
  .left-spacing {
    padding-left: 10px;
  }
  .all-licenses-table-footer {
    margin-top: 20px;
  }
  .export-link {
    display: inline-block;
    margin-left: -15px;
    margin-top: 15px;
  }
  .pagination-container {
    margin-right: -15px;
    display: inline-block;
  }
  select[disabled] {
    background-color: $disabledGray;
    color: white;
    cursor: not-allowed;
  }
  .dynamic-column-select-container {
    position: relative;
    flex: 0 0 340px;
    max-width: 340px;
  }
  .dynamic-column-select-container:not(.header) {
    overflow-x: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .dynamic-column-select {
    opacity: 0;
    position: absolute;
    left: 0;
    top: 1px;
    cursor: pointer;
  }
  .fa-calendar {
    font-size: 25px;
    position: absolute;
    right: 5px;
    bottom: 5px;
  }
  .page-item .page-link,
  .page-item.active .page-link {
    text-decoration: none;
  }
  .error-row {
    border-color: $danger;
  }
  .error-row-text {
    color: $danger;
    white-space: normal;
  }
  .custom-control-inline {
    margin-right: 0;
    padding: 3px;
  }
  .checkbox-col {
    flex: 0 0 16px;
  }
  .no-records-container {
    background-color: $lightGray;
    padding: 20px;
    margin-top: 20px;
    margin-bottom: 20px;
  }
  .no-records-container h3 {
    font-weight: 400;
  }
  .indent-text {
    margin-left: -10px;
  }
  .allocate-col {
    flex: 0 0 150px;
  }
  select[disabled]:not(.select-action-dropdown) {
    background-color: transparent;
    color: #202020;
  }
  .modal-content-container h5 {
    font-size: 26px;
    font-weight: 400;
  }
  .modal-content-container h6 {
    font-size: 20px;
    font-weight: 400;
  }
  .odd-row {
    background-color: $lightPrimary;
  }
  .date-picker .form-control:disabled, .form-control[readonly] {
    background: $white;
    border-color: transparent;
  }
  .not-permitted-text {
      border-radius: 5px;
      margin: 10px 0 10px 0;
      padding: 20px 10px 20px 10px;
      background-color: red;
      color: white;
    }
  @media (min-width: 768px) {
    .options-container {
      height: auto;
      opacity: 1;
    }
    .page-size-col {
      max-width: 120px;
      flex: 0 0 120px;
    }
    .pagination-container {
      display: block;
      float: right;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/AllLicenses.vue